/* eslint-disable @typescript-eslint/promise-function-async */
import {
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tooltip,
  ConfigProvider,
  Radio,
  Card,
  type RadioChangeEvent,
} from "antd";
import { WarningOutlined } from "@ant-design/icons";
import React, { useEffect, useRef, useState, type ReactElement } from "react";
import {
  formatCropsData,
  groupSeedIdsByLocalizedCropId,
  mapCatalogCropsToLegacyShape,
  restrictToTwoDecimals,
} from "../utils";
import { useCreateProtocolForm } from "../index";
import { useSearchParams } from "react-router-dom";
import AxiosInstance from "../../../shared/utils/axios";
import { fetchProtocolTemplateData } from "../../protocols/protocolsService";
import PlatFormAPIAxiosInstance from "../../../shared/utils/platFormAxios";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import type { protocolCountries } from "../shared/interfaces/product-interfaces";
import { DropdownEmptyState } from "../../../shared/layout/DropdownEmptyState";
import infoIcon from "../../../assets/images/info.svg";
import plotIcon from "../../../../src/assets/images/plot-grey.svg";
import plotIconActive from "../../../../src/assets/images/plot-blue.svg";
import stripIcon from "../../../../src/assets/images/split-grey.svg";
import stripIconActive from "../../../../src/assets/images/split-blue.svg";
import { useProtocolsRouter } from "../../../shared/custom-hooks/useProtocolsRouter";
import "../step-components/BasicInfoComponent.less";

const MIN_ALLOWABLE_PLOT_SIZE = 1;

// Normalize crop names for comparison: treat underscores/hyphens as spaces,
// collapse whitespace, lowercase. Guards against false mismatches like
// "Corn " vs "corn" or "Winter_Wheat" vs "Winter Wheat".
const normalizeName = (name: unknown): string =>
  String(name ?? "")
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();

const BasicInfoComponent = (): ReactElement => {
  const { t } = useTranslation();
  const { form, formValidationData, setFormValidationData } =
    useCreateProtocolForm();
  const [searchParams] = useSearchParams();

  const protocolId = searchParams.get("protocol_id");
  const templateId = searchParams.get("template_id");
  // When the protocol originated from a template, changing the country must
  // surface the "Change Country Alert" on the Rules & Products screens.
  const isFromTemplate = Boolean(templateId);
  const { Option } = Select;
  const { TextArea } = Input;

  const showCountryChangeAlert = Boolean(
    formValidationData?.showCountryChangeAlert
  );

  const [existingData, setExistingData] = useState<any>(null);
  const [countries, setCountries] = useState<protocolCountries[]>([]);
  const [countryCodes, setCountryCodes] = useState<string | null>(null);
  const [cropContry, setCropCountry] = useState<any[]>([]);
  const [cropLoading, setCropLoading] = useState<boolean>(false);
  const { workspaceId, orgId } = useProtocolsRouter();
  const [trialMethod, setTrialMethod] = useState<string>("plot");
  type TrialMethod = "plot" | "strip";

  // Holds the crop NAME selected before a country change while we wait for the
  // new country's crop list to load. Null = no pending check.
  const pendingCropNameRef = useRef<string | null>(null);

  // Monotonically increasing id for every crop-list fetch. If the user
  // changes country again before a fetch resolves, only the LATEST fetch's
  // result is allowed to update state -- otherwise an older, slower response
  // can land after the newer one and silently overwrite it with stale data.
  const latestCropFetchReqRef = useRef<number>(0);

  // Original (template-default) selections, captured on first load. Used to
  // restore crop + products when the user returns to the original country.
  const originalCountryRef = useRef<string | null>(null);
  const originalCropRef = useRef<{ name: string; id: string } | null>(null);

  // Monotonic generation counter for crop/country selection intent. Bumped by
  // every user action that decides "this is the crop/country the user wants"
  // (picking a crop manually, returning to the original country, starting a
  // new country change). `applyTemplateCountryChange` captures the generation
  // in effect when IT starts; if the user has since made a newer decision (the
  // ref has moved on) by the time its async backend response lands, that
  // response is stale and must not overwrite the newer choice -- without this,
  // manually picking a crop while the country's own template re-resolution is
  // still in flight gets silently reverted the moment that slower response
  // arrives, reintroducing the OLD crop's products (see CWTRC bug: template
  // crop's products stayed selected after switching to a different crop).
  const cropSelectionGenRef = useRef<number>(0);

  const getProtocolData = async (): Promise<any> => {
    try {
      const response = await AxiosInstance.get(
        `/protocol/${protocolId as string}`,
        {
          headers: {
            "org-id": orgId ?? "",
            "workspace-id": workspaceId ?? "",
          },
        }
      );
      setExistingData(response.data);
      // Set trial method based on existing data
      if (response.data?.trial_application_method) {
        setTrialMethod(response.data.trial_application_method);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (protocolId) {
      getProtocolData().catch((error) => {
        console.error("Error fetching data:", error);
      });
    } else if (!isFromTemplate) {
      // For a genuinely new (non-template) protocol, default to plots. A
      // template flow must NOT be forced to "plot" here -- the template's own
      // `trial_application_method` (and strip width values, if applicable)
      // arrive asynchronously via the wizard's `fetchTemplateData` effect and
      // are picked up by the form-watch sync below instead.
      setTrialMethod("plot");
      form.setFieldsValue({
        trial_application_method: "plot",
      });
    }
  }, [protocolId, isFromTemplate]);

  // Template flow: `trial_application_method` (and strip width values) are
  // written directly into the form by the wizard's `fetchTemplateData` effect,
  // not by this component. Watch the field so the local `trialMethod` state --
  // which actually drives which card is highlighted and which fields render --
  // picks up the template's real selection once it arrives, instead of staying
  // stuck on the "plot" default above.
  const watchedTrialMethod = Form.useWatch("trial_application_method", form);

  useEffect(() => {
    if (
      watchedTrialMethod &&
      (watchedTrialMethod as TrialMethod) !== trialMethod
    ) {
      setTrialMethod(watchedTrialMethod as TrialMethod);
    }
  }, [watchedTrialMethod]);

  useEffect(() => {
    if (existingData) {
      // Saving the Rules step nulls out whichever type (plot/strip) isn't
      // currently selected (see index.tsx's handleStepTwo), so a saved
      // protocol only ever carries real size data for its CURRENT type. Fall
      // back to the immutable template snapshot for the other type, so its
      // original values reappear if the user switches back to it, instead of
      // staying blank.
      const originalSizeSnapshot = formValidationData?.originalTemplateSnapshot;
      const plotMinimumSize =
        existingData?.plot_minimum_size ??
        originalSizeSnapshot?.plotMinimumSize;
      const plotRecommendedSize =
        existingData?.plot_recommended_size ??
        originalSizeSnapshot?.plotRecommendedSize;
      const stripSize =
        existingData?.strip_size ?? originalSizeSnapshot?.stripSize;

      form.setFieldsValue({
        protocolName: existingData?.name,
        protocolDescription: existingData?.description,
        protocolCountry: existingData?.crop_details?.country,
        protocolCountries: existingData?.crop_details?.countries,
        protocolCropId: existingData?.crop_details?.crop_id,
        protocolCrop: existingData?.crop,
        plotMinLength: plotMinimumSize?.length,
        plotMinWidth: plotMinimumSize?.width,
        plotRecommendedLength: plotRecommendedSize?.length,
        plotRecommendedWidth: plotRecommendedSize?.width,
        stripMinWidth: stripSize?.min_width,
        stripMaxWidth: stripSize?.max_width,
        trial_application_method:
          existingData?.trial_application_method || "plot",
      });

      // Capture the original country + crop once, for later restore when the
      // user returns to the original country.
      originalCountryRef.current = existingData?.crop_details?.country ?? null;
      originalCropRef.current = {
        name: existingData?.crop,
        id: existingData?.crop_details?.crop_id,
      };

      fetchData(existingData?.crop_details?.country).catch((error) => {
        console.error("Error fetching data:", error);
      });
    }
  }, [existingData]);

  // Fresh-template flow: there is no protocol_id, so `existingData` is null and
  // the refs above never get set. Seed them from the immutable template
  // snapshot captured by the provider so "return to original country" works.
  useEffect(() => {
    if (!isFromTemplate) return;
    if (originalCountryRef.current !== null) return;

    const snap = formValidationData?.originalTemplateSnapshot;
    if (!snap) return;

    originalCountryRef.current = snap.country ?? null;
    originalCropRef.current = {
      name: snap.cropName,
      id: snap.cropId,
    };

    // The wizard (index.tsx) writes protocolCountry/protocolCrop straight into
    // the form via form.setFieldsValue when loading a template -- that
    // bypasses this component's `handleCountries` handler entirely, so
    // `countryCodes` (the only thing that drives the crop-catalog fetch below)
    // was never set. Without it, the Crop dropdown never loads the country's
    // crop list and only shows the template's already-selected crop. Seed it
    // here so the catalog loads for the template's own country up front.
    if (snap.country) {
      setCountryCodes(snap.country);
    }
  }, [isFromTemplate, formValidationData?.originalTemplateSnapshot]);

  // Template flow, country changed away from the template's original country:
  // ask the backend to resolve this template's crop/products for the NEW
  // country directly, instead of guessing via a generic country crop list +
  // name matching. The backend already does the product-level match/mismatch
  // resolution itself -- a mismatched product comes back with `id`/`variety`
  // nulled out (same array length, just voided entries) -- so we pass that
  // result straight through to ProductsSeedComponent rather than re-deriving
  // it from a separate catalog fetch + name-match heuristic.
  const applyTemplateCountryChange = async (
    newCountry: string
  ): Promise<void> => {
    // Claim the current generation BEFORE awaiting. If the user makes a newer
    // crop/country decision (bumping the ref) before this resolves, `myGen`
    // below will no longer match and this response must be discarded instead
    // of overwriting that newer decision.
    const myGen = ++cropSelectionGenRef.current;
    try {
      const data = await fetchProtocolTemplateData(
        templateId ?? "",
        orgId ?? "",
        workspaceId ?? "",
        newCountry
      );

      if (myGen !== cropSelectionGenRef.current) return;

      const cropId = data?.crop_details?.crop_id;
      // `localized_crop_ids` is a DIFFERENT id space than `crop_id` (confirmed
      // from a live response: crop_id "6ea2f74f-..." vs localized_crop_ids
      // "a7e4b029-..." for the same crop/country) -- the seed catalog query
      // only understands the localized id, so never fall back to `crop_id`.
      const localizedCropIds = Array.isArray(
        data?.crop_details?.localized_crop_ids
      )
        ? data.crop_details.localized_crop_ids
        : [];

      if (cropId) {
        form.setFieldsValue({
          protocolCrop: data?.crop,
          protocolCropId: cropId,
        });
        setFormValidationData((prev: any) => ({
          ...(prev ?? {}),
          cropRevalidationInProgress: false,
          clearProductsAndCalculations: false,
          seedRevalidationRequired: false,
          // CP/Biologicals has its own independent, already-correct
          // country-aware revalidation pipeline in SelectCpProduct.tsx (fetch
          // by country, name-match, clear-all-on-mismatch) -- it just needs
          // this flag to fire. Bio products are not part of the
          // `product_details.seeds` payload this component fetches, so this
          // flag (not `countryChangeSeeds`) is what drives it.
          bioRevalidationRequired: true,
          revalidationCropId: cropId,
          revalidationLocalizedCropIds: localizedCropIds,
          revalidationCountry: newCountry,
          // Authoritative product-level match result from the backend, for
          // ProductsSeedComponent to apply as-is.
          countryChangeSeedsPending: true,
          countryChangeSeeds: data?.product_details?.seeds ?? null,
        }));
      } else {
        form.setFieldsValue({
          protocolCrop: "",
          protocolCropId: undefined,
        });
        setFormValidationData((prev: any) => ({
          ...(prev ?? {}),
          cropRevalidationInProgress: false,
          clearProductsAndCalculations: true,
          seedRevalidationRequired: false,
          bioRevalidationRequired: false,
          // Keep set to the new country (NOT null): the country itself is
          // still valid even though this crop doesn't exist there, and
          // ProductsBiologicalComponents reads this as the authoritative
          // country for its own (crop-independent) catalog fetch. It also
          // permanently tells ProductsSeedComponent's persisted-draft hydration
          // to stand down for the rest of this session.
          revalidationCountry: newCountry,
          countryChangeSeedsPending: false,
          countryChangeSeeds: null,
        }));
      }
    } catch (error) {
      console.error("Error fetching template data for country change:", error);
      // Without this, a failed/timed-out request leaves formValidationData
      // untouched: ProductsSeedComponent never hears about the country change
      // (no clearProductsAndCalculations, no countryChangeSeedsPending), so it
      // never calls getProductList -- the only place that ever clears
      // `loading` -- and the Seeds tab spins forever. Fail safe into the same
      // "can't confirm this crop/selection is valid here" clear used for a
      // genuine crop mismatch, rather than leaving the UI stuck.
      form.setFieldsValue({
        protocolCrop: "",
        protocolCropId: undefined,
      });
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        cropRevalidationInProgress: false,
        clearProductsAndCalculations: true,
        seedRevalidationRequired: false,
        bioRevalidationRequired: false,
        revalidationCountry: newCountry,
        countryChangeSeedsPending: false,
        countryChangeSeeds: null,
      }));
    }
  };

  const handleCountries = (value: string): void => {
    // Template flow: returning to the ORIGINAL country -> restore the original
    // crop + products and clear the alert. No revalidation/clear needed.
    if (
      isFromTemplate &&
      value !== "" &&
      value === originalCountryRef.current
    ) {
      // This IS the newest crop/country decision -- invalidate any
      // still-in-flight applyTemplateCountryChange from a prior country pick
      // so its (now stale) response can't clobber this restore.
      cropSelectionGenRef.current += 1;

      const countryName = countries?.find(
        (item) => item.iso3166_alpha2_code === value
      )?.country_name;

      pendingCropNameRef.current = null;
      setCountryCodes(value);

      form.setFieldsValue({
        protocolCountries: countryName ? [countryName] : [],
        protocolCountry: value,
        protocolCrop: originalCropRef.current?.name,
        protocolCropId: originalCropRef.current?.id,
      });

      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        cropRevalidationInProgress: false,
        showCountryChangeAlert: false,
        restoreOriginalSeeds: true,
        restoreOriginalBio: true,
        clearProductsAndCalculations: false,
        seedRevalidationRequired: false,
        bioRevalidationRequired: false,
        revalidationCountry: value,
        // Original localized crop ids so the seed restore can refetch the
        // correct (original-country) catalog in the fresh-template flow.
        revalidationLocalizedCropIds:
          prev?.originalTemplateSnapshot?.localizedCropIds ?? [],
      }));
      return;
    }

    // Moving AWAY from the original (or to any other country) -> raise alert
    // and re-resolve the template against the new country via the backend
    // (authoritative), instead of the generic crop-list + name-match heuristic
    // used by the non-template flow below.
    if (isFromTemplate) {
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        showCountryChangeAlert: true,
      }));
      if (value !== "") {
        // Whether this template's crop/products are still valid for the new
        // country isn't known until applyTemplateCountryChange resolves --
        // block the wizard's next-step button until then (cleared inside
        // applyTemplateCountryChange's success/failure branches).
        setFormValidationData((prev: any) => ({
          ...(prev ?? {}),
          cropRevalidationInProgress: true,
        }));
        void applyTemplateCountryChange(value);
      }
    }
    if (value !== "") {
      // Non-template flow: remember the currently selected crop so the match
      // effect can try to preserve it once the new country's crop list loads.
      if (!isFromTemplate) {
        pendingCropNameRef.current =
          (form.getFieldValue("protocolCrop") as string) ?? null;
        // Whether the previously selected crop is still valid for the new
        // country isn't known until the crop list (+ varieties) reloads and
        // the match effect below resolves it. Block the wizard's next-step
        // button until then so the user can't advance on a crop that's about
        // to be cleared -- see the matching setFormValidationData at the end
        // of the match effect for where this gets cleared. Only do this when
        // there's actually a pending crop to check: when pendingCropNameRef
        // is null (e.g. a brand-new protocol's first country pick, nothing
        // selected yet) the match effect bails out at its top guard and never
        // reaches that clearing line, which would leave this flag stuck true
        // forever.
        if (pendingCropNameRef.current !== null) {
          setFormValidationData((prev: any) => ({
            ...(prev ?? {}),
            cropRevalidationInProgress: true,
          }));
        }
      }
      const countryName = countries?.find(
        (item) => item.iso3166_alpha2_code === value
      )?.country_name;
      form.setFieldsValue({
        protocolCountries: [countryName],
        protocolCountry: value,
      });
      setCountryCodes(value);
    } else {
      form.setFieldsValue({
        protocolCountry: undefined,
      });
      setCountryCodes("");
      setCropCountry([]);
      pendingCropNameRef.current = null;
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        cropRevalidationInProgress: false,
      }));
    }
  };

  const ApiCall = async (country: string): Promise<any> => {
    try {
      setCropLoading(true);
      const catalogCrops: any[] = [];
      let page = 1;
      let isLastPage = false;
      while (!isLastPage) {
        const response = await PlatFormAPIAxiosInstance.get(
          `catalog/crops?include_deleted_crop=false&country=${
            country ?? ""
          }&page=${page}&size=200`
        );
        catalogCrops.push(...(response?.data?.content ?? []));
        isLastPage = response?.data?.last ?? true;
        page += 1;
      }
      return mapCatalogCropsToLegacyShape(catalogCrops, country);
    } catch (error) {
      console.error("Error While Fetching Data", error);
      return [];
    }
  };

  // Batches a single POST /catalog/seeds/query across every crop's localized
  // id to determine which crops have seed varieties for this country -- the
  // replacement for the `varieties_ids` field the legacy /v2/crops response
  // used to carry inline. Returns null (rather than {}) on failure so the
  // caller can tell "confirmed no varieties" apart from "couldn't check".
  const fetchCropVarietiesAvailability = async (
    crops: any[]
  ): Promise<Record<string, string[]> | null> => {
    const localizedCropIds = crops
      .map((crop: any) => crop?.id)
      .filter((id: any): id is string => Boolean(id));
    if (localizedCropIds.length === 0) return {};
    try {
      const seeds: any[] = [];
      let page = 1;
      let isLastPage = false;
      while (!isLastPage) {
        const response = await PlatFormAPIAxiosInstance.post(
          `catalog/seeds/query?page=${page}&size=1000`,
          { localized_crop_id: localizedCropIds, show_canonical: true }
        );
        seeds.push(...(response?.data?.content ?? []));
        isLastPage = response?.data?.last ?? true;
        page += 1;
      }
      return groupSeedIdsByLocalizedCropId(seeds);
    } catch (error) {
      console.error("Error While Fetching Seed Varieties", error);
      return null;
    }
  };

  const fetchData = async (countryCodes: string): Promise<void> => {
    // Claim a request id BEFORE awaiting. If another country change starts
    // (and claims a higher id) before this fetch resolves, this fetch is
    // stale and must not be allowed to apply its result.
    const reqId = ++latestCropFetchReqRef.current;
    const CropData = [countryCodes].map(
      async (item: any) => await ApiCall(item)
    );
    try {
      const res = await Promise.all(CropData);
      const crops = res.flat().filter(Boolean);
      const varietiesByCropId = await fetchCropVarietiesAvailability(crops);

      if (reqId !== latestCropFetchReqRef.current) {
        // A newer country change superseded this fetch -- discard.
        return;
      }

      const cropsWithVarieties =
        varietiesByCropId === null
          ? crops
          : crops.map((crop: any) => ({
              ...crop,
              varieties_ids: varietiesByCropId[crop?.id] ?? [],
            }));
      setCropLoading(false);
      setCropCountry(cropsWithVarieties);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!countryCodes) return;

    // Load the new country's crop list. The keep-vs-clear decision is made in
    // the effect below once cropContry resolves (works for both flows now).
    setCropCountry([]);
    fetchData(countryCodes).catch((error) => {
      console.error("Error fetching data:", error);
    });
  }, [countryCodes]);

  // After the new country's crop list loads, decide whether to preserve the
  // previously selected crop (Scenario A) or clear it + products (Scenario B).
  useEffect(() => {
    if (pendingCropNameRef.current === null) return;
    if (cropLoading) return;

    const prevCropName = pendingCropNameRef.current;
    pendingCropNameRef.current = null;

    // Normalized, case/whitespace-insensitive name match against the new
    // country's crop catalog.
    const targetName = normalizeName(prevCropName);
    const matches = cropContry.filter(
      (c: any) => normalizeName(c?.name) === targetName
    );

    // When several crops share the same name, prefer one that actually has
    // varieties so we don't bind to an unusable entry.
    const matchedCrop =
      matches.find(
        (c: any) =>
          Array.isArray(c?.varieties_ids) && c.varieties_ids.length > 0
      ) ?? matches[0];

    if (matches.length > 1) {
      console.warn(
        `[BasicInfo] Multiple crops named "${prevCropName ?? ""}" found for `,
        `country "${countryCodes ?? ""}". Selected id: ${String(
          matchedCrop?.id ?? ""
        )}`
      );
    }

    if (matchedCrop) {
      // Scenario A: crop name still exists. Keep it, remap the id to the new
      // country's crop id, and (template flow) ask the product screens to
      // re-validate their selections against the new country's catalog.
      form.setFieldsValue({
        protocolCrop: matchedCrop.name,
        protocolCropId: matchedCrop.id,
      });

      if (isFromTemplate) {
        setFormValidationData((prev: any) => ({
          ...(prev ?? {}),
          seedRevalidationRequired: true,
          bioRevalidationRequired: true,
          clearProductsAndCalculations: false,
          revalidationCropId: matchedCrop.id,
          // The seed catalog's `localized_crop_id` filter expects the crop's
          // OWN id (matching `crop_details.localized_crop_ids` on a persisted
          // protocol, which is just `[crop_id]`) -- NOT `varieties_ids` (a
          // list of hundreds of unrelated product/variety ids used only to
          // flag "does this crop have any products" for the dropdown). Using
          // `varieties_ids` here made the catalog query return zero results
          // every time, so every previously selected product looked
          // mismatched and got cleared even when it should have been kept.
          revalidationLocalizedCropIds: matchedCrop.id ? [matchedCrop.id] : [],
          // New ISO country code so the biological product screen fetches the
          // correct catalog when revalidating.
          revalidationCountry: countryCodes,
        }));
      }
    } else {
      // Scenario B: crop no longer exists -> clear crop (and, template flow,
      // all products/rates).
      form.setFieldsValue({
        protocolCrop: "",
        protocolCropId: undefined,
      });

      if (isFromTemplate) {
        setFormValidationData((prev: any) => ({
          ...(prev ?? {}),
          clearProductsAndCalculations: true,
          seedRevalidationRequired: false,
          bioRevalidationRequired: false,
          // Clear any stale revalidation country so it can't leak forward.
          revalidationCountry: null,
        }));
      }
    }

    // The keep-vs-clear decision above is now reflected in the form, so the
    // next-step button can safely re-evaluate and unblock.
    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      cropRevalidationInProgress: false,
    }));
  }, [cropContry, cropLoading]);

  const cropOptions = formatCropsData(cropContry);

  const sortedCropOptions = [...cropOptions].sort((a: any, b: any) =>
    a?.label?.toLowerCase().localeCompare(b?.label?.toLowerCase())
  );

  const getCountries = async (): Promise<any> => {
    try {
      const response = await PlatFormAPIAxiosInstance.get(
        "/utils/geo/regulatory-zones"
      );
      setCountries(response.data?.content);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getCountries().catch((error) => {
      console.error("Error fetching data:", error);
    });
  }, []);

  function validateLength(
    rule: any,
    value: string,
    callback: any,
    maxWords: number
  ): void {
    if (!value) {
      callback();
      return;
    }
    const wordCount = value.length;
    if (wordCount >= maxWords) {
      maxWords === 100
        ? callback(
            formatTranslation(t("createProtocol.basicInfo.protocolNameMessage"))
          )
        : callback(
            formatTranslation(
              t("createProtocol.basicInfo.protocolDescriptionMessage")
            )
          );
    } else {
      callback();
    }
  }

  // Add this handler for Radio.Group
  const handleRadioGroupChange = (e: RadioChangeEvent): void => {
    const value = e.target.value as TrialMethod;
    setTrialMethod(value);
    form.setFieldsValue({
      trial_application_method: value,
    });
  };

  return (
    <div className="basicInfo-component">
      <div className="basicInfo-container">
        <h2>{formatTranslation(t("createProtocol.basicInfo.rule"))}</h2>
        <p>{formatTranslation(t("createProtocol.basicInfo.description"))}</p>

        <div className="basicInfo-form-section">
          <h4>
            {formatTranslation(t("createProtocol.basicInfo.protocolDetail"))}
          </h4>
          <Form.Item
            key="name"
            className="basic-info-form-item"
            name="protocolName"
            label={formatTranslation(
              t("createProtocol.basicInfo.protocolName")
            )}
            rules={[
              { required: true },
              {
                validator: (rule, value, callback) => {
                  validateLength(rule, value, callback, 100);
                },
              },
            ]}
          >
            <Input
              placeholder={formatTranslation(
                t("createProtocol.basicInfo.protocolNamePlaceholder")
              )}
              maxLength={100}
              onChange={(e) => {
                const wordCount = e.target.value.length;
                if (wordCount <= 100) {
                  form.setFieldsValue({ protocolName: e.target.value });
                }
              }}
            ></Input>
          </Form.Item>

          <Form.Item
            key="protocolDescription"
            name="protocolDescription"
            className="basic-info-form-item"
            label={formatTranslation(
              t("createProtocol.basicInfo.protocolDescription")
            )}
            rules={[
              {
                validator: (rule, value, callback) => {
                  validateLength(rule, value, callback, 500);
                },
              },
            ]}
          >
            <TextArea
              placeholder={formatTranslation(
                t("createProtocol.basicInfo.protocolDesPlaceholder")
              )}
              maxLength={500}
              rows={3}
              onChange={(e) => {
                const wordCount = e.target.value.length;
                if (wordCount <= 500) {
                  form.setFieldsValue({ protocolDescription: e.target.value });
                }
              }}
            ></TextArea>
          </Form.Item>

          <Flex gap={24}>
            <ConfigProvider
              renderEmpty={() => <DropdownEmptyState isloading={cropLoading} />}
            >
              <Form.Item
                key="protocolCountry"
                name="protocolCountry"
                className="basic-info-form-item"
                label="Country"
                style={{ width: "100%" }}
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  size="large"
                  optionFilterProp="label"
                  placeholder={formatTranslation(
                    t("createProtocol.basicInfo.protocolCountryPlaceholder")
                  )}
                  onChange={handleCountries}
                >
                  {countries.map((country: protocolCountries) => (
                    <Option
                      key={country?.id}
                      value={country?.iso3166_alpha2_code}
                      label={country?.country_name}
                    >
                      <span className="country-code">
                        {country?.iso3166_alpha2_code}
                      </span>{" "}
                      {country?.country_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </ConfigProvider>

            <ConfigProvider
              renderEmpty={() => <DropdownEmptyState isloading={cropLoading} />}
            >
              <Form.Item
                key="protocolCrop"
                name="protocolCrop"
                className="basic-info-form-item"
                label={formatTranslation(
                  t("createProtocol.basicInfo.protocolCrop")
                )}
                style={{ width: "100%" }}
                rules={[{ required: true }]}
              >
                <Select
                  loading={cropLoading}
                  showSearch
                  size="large"
                  allowClear
                  optionFilterProp="label"
                  placeholder={formatTranslation(
                    t("createProtocol.basicInfo.protocolCropPlaceholder")
                  )}
                  disabled={countryCodes === ""}
                  onChange={(value) => {
                    // This IS the newest crop/country decision -- invalidate
                    // any still-in-flight applyTemplateCountryChange (kicked
                    // off by an earlier country pick) so its stale response
                    // can't overwrite the crop the user is choosing right now.
                    cropSelectionGenRef.current += 1;

                    const cropName = sortedCropOptions.find(
                      (item) => item?.value === value
                    );
                    form.setFieldsValue({
                      protocolCropId: value,
                      protocolCrop: cropName?.label,
                    });

                    // Manually picking a crop from this dropdown must always
                    // clear/revalidate the previously selected products -- a
                    // product valid for the OLD crop has no guarantee of being
                    // valid for the new one. This used to be gated behind
                    // `isFromTemplate`, so editing a plain (non-template)
                    // protocol's crop left the old crop's seed/CP selections
                    // and their density/rate values selected and persisted
                    // even though they no longer belonged to the new crop (see
                    // CWTRC bug report: crop changed Corn -> Winter Wheat kept
                    // SY_BRENTON/SY_IMPULSE/SY_TALISMAN selected with their
                    // density ranges intact). The downstream effects that
                    // consume these flags (ProductsSeedComponent,
                    // SelectCpProduct) already react to them unconditionally,
                    // so this only needed to stop being skipped here.
                    //
                    // Also mirrors the same Scenario A/B the country-change
                    // match effect below does, using `value` directly as the
                    // crop id (dropdown options are built with
                    // `value: crop.id`, same shape the match effect uses as
                    // `matchedCrop.id`).
                    if (value) {
                      setFormValidationData((prev: any) => ({
                        ...(prev ?? {}),
                        seedRevalidationRequired: true,
                        bioRevalidationRequired: true,
                        clearProductsAndCalculations: false,
                        revalidationCropId: value,
                        revalidationLocalizedCropIds: [value],
                        revalidationCountry: countryCodes,
                        // A country change may have ALREADY resolved the
                        // template's OWN crop against this country and stashed
                        // those (old-crop) products in countryChangeSeeds
                        // (with countryChangeSeedsPending=true). The user is
                        // now picking a DIFFERENT crop, so that resolution is
                        // for the wrong crop -- drop it, or the seed
                        // country-change effect would apply the old crop's
                        // products regardless of the crop just chosen. The
                        // crop-scoped seedRevalidationRequired path above then
                        // owns the keep/clear decision against the new crop's
                        // catalog.
                        countryChangeSeedsPending: false,
                        countryChangeSeeds: null,
                      }));
                    } else {
                      setFormValidationData((prev: any) => ({
                        ...(prev ?? {}),
                        clearProductsAndCalculations: true,
                        seedRevalidationRequired: false,
                        bioRevalidationRequired: false,
                        revalidationCountry: null,
                        countryChangeSeedsPending: false,
                        countryChangeSeeds: null,
                      }));
                    }
                  }}
                  options={sortedCropOptions}
                  optionRender={(option) => (
                    <Space
                      style={{ width: "100%", justifyContent: "space-between" }}
                    >
                      <span>{option?.data?.label}</span>
                      {!option?.data?.hasVarieties && (
                        <span className="crop-disabled-option-style">
                          {formatTranslation(
                            t("createProtocol.basicInfo.noVarietiesForCountry")
                          )}
                        </span>
                      )}
                    </Space>
                  )}
                />
              </Form.Item>
            </ConfigProvider>
          </Flex>

          {/* Change Country Alert - shown directly under Country/Crop fields */}
          {showCountryChangeAlert && (
            <div className="change-country-alert">
              <div className="change-country-alert__header">
                <WarningOutlined className="change-country-alert__icon" />
                <span className="change-country-alert__title">
                  Change Country Alert
                </span>
              </div>
              <p className="change-country-alert__body">
                Crop and product names vary by country. Changing the country may
                change the selections if an automatic match fails. Please review
                the selections and add missing items manually.
              </p>
            </div>
          )}

          {/* Trial Application Method Section */}
          <div className="trial-method-container">
            <div className="trial-method-section">
              <Form.Item
                name="trial_application_method"
                className="trial_application_method"
                label={formatTranslation(
                  t("createProtocol.basicInfo.trialApplicationMethod")
                )}
                rules={[{ required: true }]}
              >
                <Radio.Group
                  onChange={handleRadioGroupChange}
                  value={trialMethod}
                  className="trial-method-radio-group"
                >
                  <Card
                    className={`trial-method-card ${
                      trialMethod === "plot" ? "active" : ""
                    }`}
                    onClick={() => {
                      setTrialMethod("plot");
                      form.setFieldsValue({ trial_application_method: "plot" });
                    }}
                  >
                    <div className="trial-method-content">
                      <img
                        className="trial-method-icon"
                        src={trialMethod === "plot" ? plotIconActive : plotIcon}
                        alt="Plot"
                      />
                      <div className="trial-method-text">
                        <Radio value="plot">
                          {formatTranslation(
                            t("createProtocol.basicInfo.plotsOption")
                          )}
                        </Radio>
                        <p className="trial-method-description">
                          {formatTranslation(
                            t("createProtocol.basicInfo.plotsDescription")
                          )}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card
                    className={`trial-method-card ${
                      trialMethod === "strip" ? "active" : ""
                    }`}
                    onClick={() => {
                      setTrialMethod("strip");
                      form.setFieldsValue({
                        trial_application_method: "strip",
                      });
                    }}
                  >
                    <div className="trial-method-content">
                      <img
                        className="trial-method-icon"
                        src={
                          trialMethod === "strip" ? stripIconActive : stripIcon
                        }
                        alt="Strip"
                      />
                      <div className="trial-method-text">
                        <Radio value="strip">
                          {formatTranslation(
                            t("createProtocol.basicInfo.stripsOption")
                          )}
                        </Radio>
                        <p className="trial-method-description">
                          {formatTranslation(
                            t("createProtocol.basicInfo.stripsDescription")
                          )}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Radio.Group>
              </Form.Item>
            </div>

            {/* Plot Size Fields (shown when plots is selected) */}
            {trialMethod === "plot" && (
              <Flex gap={24} justify="space-between">
                <div className="basicinfo-plot">
                  <div className="form-plot-size">
                    {formatTranslation(
                      t("createProtocol.basicInfo.plotMinimumSize")
                    )}{" "}
                    <span>
                      <Tooltip
                        placement="right"
                        title={formatTranslation(
                          t("createProtocol.basicInfo.plotMinimumSizeTooltip")
                        )}
                      >
                        <img
                          className="plotsize-info-icon"
                          src={infoIcon}
                          alt="info icon"
                        />
                      </Tooltip>
                    </span>
                  </div>
                  <Flex gap={12} justify="space-between">
                    <Form.Item
                      key="plotMinWidth"
                      name="plotMinWidth"
                      className="basicInfo-input-num-form-item"
                      label={formatTranslation(
                        t("createProtocol.basicInfo.width")
                      )}
                      dependencies={["plotRecommendedWidth"]}
                      rules={[
                        { required: true },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const recommendedWidth = getFieldValue(
                              "plotRecommendedWidth"
                            );
                            if (
                              !value ||
                              !recommendedWidth ||
                              value <= recommendedWidth
                            ) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                formatTranslation(
                                  t(
                                    "createProtocol.basicInfo.plotMinimumSizeMessage"
                                  )
                                )
                              )
                            );
                          },
                        }),
                      ]}
                    >
                      <InputNumber
                        controls={true}
                        size="large"
                        min={1}
                        max={100000}
                        step={0.01}
                        precision={2}
                        className="basicInfo-input-num"
                        onKeyDown={restrictToTwoDecimals}
                      />
                    </Form.Item>
                    <Form.Item
                      key="plotMinLength"
                      name="plotMinLength"
                      className="basicInfo-input-num-form-item"
                      label={formatTranslation(
                        t("createProtocol.basicInfo.length")
                      )}
                      dependencies={["plotRecommendedLength"]}
                      rules={[
                        { required: true },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const recommendedLength = getFieldValue(
                              "plotRecommendedLength"
                            );
                            if (
                              !value ||
                              !recommendedLength ||
                              value <= recommendedLength
                            ) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                formatTranslation(
                                  t(
                                    "createProtocol.basicInfo.plotMinimumSizeMessage"
                                  )
                                )
                              )
                            );
                          },
                        }),
                      ]}
                    >
                      <InputNumber
                        size="large"
                        min={1}
                        max={100000}
                        step={0.01}
                        precision={2}
                        className="basicInfo-input-num"
                        onKeyDown={restrictToTwoDecimals}
                      />
                    </Form.Item>
                  </Flex>
                </div>

                <div className="basicinfo-plot">
                  <div className="form-plot-size">
                    {formatTranslation(
                      t("createProtocol.basicInfo.plotRecommendedSize")
                    )}{" "}
                    <span>
                      <Tooltip
                        placement="right"
                        title={formatTranslation(
                          t(
                            "createProtocol.basicInfo.plotRecommendedSizeTooltip"
                          )
                        )}
                      >
                        <img
                          className="plotsize-info-icon"
                          src={infoIcon}
                          alt="info icon"
                        />
                      </Tooltip>
                    </span>
                  </div>
                  <Flex gap={12} justify="space-between">
                    <Form.Item
                      key="plotRecommendedWidth"
                      name="plotRecommendedWidth"
                      className="basicInfo-input-num-form-item"
                      label={formatTranslation(
                        t("createProtocol.basicInfo.width")
                      )}
                      dependencies={["plotMinWidth"]}
                      rules={[
                        { required: true },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const minWidth = getFieldValue("plotMinWidth");
                            const minRequired = Math.max(
                              minWidth || 0,
                              MIN_ALLOWABLE_PLOT_SIZE
                            );
                            if (!value || value >= minRequired) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                formatTranslation(
                                  t(
                                    "createProtocol.basicInfo.plotRecommendedSizeMessage"
                                  )
                                )
                              )
                            );
                          },
                        }),
                      ]}
                    >
                      <InputNumber
                        size="large"
                        min={1}
                        max={100000}
                        step={0.01}
                        precision={2}
                        className="basicInfo-input-num"
                        onKeyDown={restrictToTwoDecimals}
                      />
                    </Form.Item>
                    <Form.Item
                      key="plotRecommendedLength"
                      name="plotRecommendedLength"
                      className="basicInfo-input-num-form-item"
                      label={formatTranslation(
                        t("createProtocol.basicInfo.length")
                      )}
                      dependencies={["plotMinLength"]}
                      rules={[
                        { required: true },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const minLength = getFieldValue("plotMinLength");
                            const minRequired = Math.max(
                              minLength || 0,
                              MIN_ALLOWABLE_PLOT_SIZE
                            );
                            if (!value || value >= minRequired) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                formatTranslation(
                                  t(
                                    "createProtocol.basicInfo.plotRecommendedSizeMessage"
                                  )
                                )
                              )
                            );
                          },
                        }),
                      ]}
                    >
                      <InputNumber
                        size="large"
                        min={1}
                        max={100000}
                        step={0.01}
                        precision={2}
                        className="basicInfo-input-num"
                        onKeyDown={restrictToTwoDecimals}
                      />
                    </Form.Item>
                  </Flex>
                </div>
              </Flex>
            )}

            {/* Strip Size Fields (shown when strips is selected) */}
            {trialMethod === "strip" && (
              <Flex gap={24} justify="space-between">
                <div className="basicinfo-strip">
                  <div className="form-strip-size">
                    {formatTranslation(
                      t("createProtocol.basicInfo.stripWidthRange")
                    )}{" "}
                    <span>
                      <Tooltip
                        placement="right"
                        title={formatTranslation(
                          t("createProtocol.basicInfo.stripWidthRangeTooltip")
                        )}
                      >
                        <img
                          className="plotsize-info-icon"
                          src={infoIcon}
                          alt="info icon"
                        />
                      </Tooltip>
                    </span>
                  </div>
                  <Flex gap={12} justify="space-between">
                    <Form.Item
                      key="stripMinWidth"
                      name="stripMinWidth"
                      className="basicInfo-input-num-form-item"
                      label={formatTranslation(
                        t("createProtocol.basicInfo.minWidth")
                      )}
                      dependencies={["stripMaxWidth"]}
                      rules={[
                        { required: true },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const maxWidth = getFieldValue("stripMaxWidth");
                            if (!value || !maxWidth || value < maxWidth) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                formatTranslation(
                                  t(
                                    "createProtocol.basicInfo.stripMinWidthMessage"
                                  )
                                )
                              )
                            );
                          },
                        }),
                      ]}
                    >
                      <InputNumber
                        controls={true}
                        size="large"
                        min={1}
                        max={100000}
                        step={0.01}
                        precision={2}
                        className="basicInfo-input-num"
                        onKeyDown={restrictToTwoDecimals}
                      />
                    </Form.Item>
                    <Form.Item
                      key="stripMaxWidth"
                      name="stripMaxWidth"
                      className="basicInfo-input-num-form-item"
                      label={formatTranslation(
                        t("createProtocol.basicInfo.maxWidth")
                      )}
                      dependencies={["stripMinWidth"]}
                      rules={[
                        { required: true },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const minWidth = getFieldValue("stripMinWidth");
                            const minRequired = Math.max(
                              minWidth || 0,
                              MIN_ALLOWABLE_PLOT_SIZE
                            );
                            if (!value || value > minRequired) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                formatTranslation(
                                  t(
                                    "createProtocol.basicInfo.stripeMaxLessThanMinMessage"
                                  )
                                )
                              )
                            );
                          },
                        }),
                      ]}
                    >
                      <InputNumber
                        size="large"
                        min={1}
                        max={100000}
                        className="basicInfo-input-num"
                        onKeyDown={(e) => {
                          restrictToTwoDecimals(
                            e,
                            form.getFieldValue("stripMaxWidth")
                          );
                        }}
                      />
                    </Form.Item>
                  </Flex>
                </div>
              </Flex>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoComponent;
